///
/// Copyright (c) 2017-present, Siklu Ltd.
/// All rights reserved.
///

import { ChangeDetectorRef, Component, NgZone, OnDestroy, OnInit } from '@angular/core';
import { Subject, takeUntil } from 'rxjs';
import { HttpErrorResponse } from '@angular/common/http';
import { GtthEventManager } from '../../services/event-manager.service';
import { GtthAlertService } from '../../services/alert.service';
import { SuiTranslateService } from '@siklu/sui-lib/localization';

@Component({
    selector: 'winde-alert-error',
    template: ` <div class="alerts" role="alert">
        @for (alert of alerts; track $index) {
            <div class="alert alert-danger" role="alert" (click)="closeAlert(alert)">{{ alert.msg }}</div>
        }
    </div>`
})
export class JhiAlertErrorComponent implements OnInit, OnDestroy {
    public alerts: any[] = [];
    private hideAlertTimer;
    private readonly destroy$: Subject<boolean> = new Subject<boolean>();

    constructor(
        private readonly alertService: GtthAlertService,
        private readonly eventManager: GtthEventManager,
        private readonly ngZone: NgZone,
        private readonly changeDetector: ChangeDetectorRef,
        private readonly translateService: SuiTranslateService
    ) {}

    ngOnInit() {
        this.eventManager
            .getEventSubscription('gtthApp.httpError')
            .pipe(takeUntil(this.destroy$))
            .subscribe((response) => {
                if (!response.content) {
                    this.closeAlert(this.alerts[0]);
                    return;
                }

                const httpResponse: HttpErrorResponse = response.content;
                switch (httpResponse.status) {
                    case 0:
                        this.addErrorAlert('Server not reachable', 'error.server.not.reachable');
                        break;
                    case 400:
                        this.handleBadRequestStatus(httpResponse);
                        break;
                    case 401:
                        this.addErrorAlert(httpResponse.error.message);
                        break;
                    case 404:
                        this.addErrorAlert('Not found', 'error.url.not.found');
                        break;
                }
            });

        this.alertService.showAlertSource$
            .pipe(takeUntil(this.destroy$))
            .subscribe((message: string | string[] | { msg: string | string[]; data: any }) => {
                if (typeof message === 'string') {
                    this.addErrorAlert(message);
                } else if (Array.isArray(message)) {
                    message.forEach((mssg: string) => {
                        this.addErrorAlert(mssg);
                    });
                } else if (typeof message === 'object') {
                    this.addErrorAlert(message.msg, message.msg, message.data);
                }
                this.changeDetector.markForCheck();
            });
    }

    ngOnDestroy() {
        this.destroy$.next(true);
        this.destroy$.unsubscribe();
        this.alerts = [];
    }

    addErrorAlert(message, key?, data?) {
        key = key || message;

        key = this.translateService.translate(key, data);

        const newAlert = this.alertService.addAlert(
            {
                type: 'danger',
                msg: key,
                params: data,
                timeout: 0,
                toast: this.alertService.isToast(),
                scoped: true
            },
            []
        );

        this.alerts = [newAlert];

        this.changeDetector.markForCheck();

        if (!(data?.timeout && data.timeout < 0)) {
            this.ngZone.runOutsideAngular(() => {
                this.hideAlertTimer = setTimeout(() => {
                    this.ngZone.run(() => {
                        this.closeAlert(newAlert);
                    });
                }, 5000);
            });
        }
    }

    closeAlert(alert) {
        const index: number = this.alerts.indexOf(alert);
        this.alerts.splice(index, 1);
        this.changeDetector.markForCheck();
        if (this.hideAlertTimer) {
            clearTimeout(this.hideAlertTimer);
            this.hideAlertTimer = undefined;
        }
    }

    private handleBadRequestStatus(httpResponse: HttpErrorResponse) {
        const headersKeysArr = httpResponse.headers.keys();
        const headers = [];

        for (const headerKey of headersKeysArr) {
            if (headerKey.endsWith('app-error') || headerKey.endsWith('app-params')) {
                headers.push(headerKey);
            }
        }

        headers.sort((a, b) => a.localeCompare(b));
        let errorHeader = null;
        let entityKey = null;
        if (headers.length > 1) {
            errorHeader = httpResponse.headers.get(headers[0]);
            entityKey = httpResponse.headers.get(headers[1]);
        }
        if (errorHeader) {
            const entityName = this.translateService.translate('global.menu.entities.' + entityKey);
            this.addErrorAlert(errorHeader, errorHeader, { entityName });
        } else if (httpResponse.error && httpResponse.error.message !== '') {
            if (httpResponse.error.params) {
                this.addErrorAlert('error.' + httpResponse.error.message, 'error.' + httpResponse.error.message, httpResponse.error.params);
            } else {
                this.addErrorAlert('error.' + httpResponse.error.message);
            }
        } else {
            this.addErrorAlert(httpResponse.message);
        }
    }
}
